/**
 * Projekty: inicjatywy i tablica zadań.
 *
 * Trzy decyzje warte zapamiętania:
 *
 * 1. **Zakres jest zdaniem, nie polem wyboru w rogu.** Tablica z zadaniami
 *    jednego produktu i tablica z zadaniami całego portfolio wyglądają tak
 *    samo, więc pomyłka kosztuje decyzję podjętą na niepełnej liście. Zakres
 *    stoi więc nad tablicą, napisany słowami, i mówi wprost, ile zadań jest
 *    w tej chwili ukrytych.
 * 2. **Zadania pobieramy raz i filtrujemy w przeglądarce.** Portfolio ma
 *    dziesiątki zadań, nie dziesiątki tysięcy. Dzięki temu licznik przy każdym
 *    produkcie jest prawdziwy także wtedy, gdy patrzymy na inny produkt —
 *    przy filtrowaniu po stronie serwera trzeba by go zgadywać.
 * 3. **Otwarte zadanie siedzi w adresie.** Powiadomienie i mail prowadzą do
 *    konkretnej karty (`/projekty?zadanie=…`), a nie do tablicy, na której
 *    trzeba jej dopiero poszukać.
 * 4. **Kolumnę zmienia się przeciągnięciem albo przyciskami.** Przyciski nie są
 *    pozostałością po braku przeciągania: przeciąganie nie działa z klawiatury
 *    i nie da się go opisać czytnikowi ekranu tak, żeby było operowalne. Nowa
 *    kolejność całej kolumny idzie na serwer jednym żądaniem, więc przerwane
 *    połączenie nie zostawia dwóch kart na tej samej pozycji.
 */

import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { RichTextEditor, RichTextView } from "../components/RichText";
import { Avatar, Card, Chip, Empty, ErrorState, Loading, SidePanel } from "../components/ui";
import { useSession } from "../hooks/useSession";
import { api } from "../lib/api";
import { useDemo } from "../lib/demo";
import { date, due, LABELS, percent } from "../lib/format";
import type { Initiative, Member, ProductBrief, Subtask, Task } from "../lib/types";

const COLUMNS: Task["status"][] = ["backlog", "todo", "doing", "review", "done"];
const TASK_TYPES: Task["type"][] = ["task", "bug", "feature_request", "chore"];
const PRIORITIES = ["p0", "p1", "p2", "p3"];
const INITIATIVE_STATUSES = ["discovery", "planned", "in_progress", "shipped", "parked", "dropped"];

/** Zadanie, którego nikt już nie tknie — nie liczy się do obciążenia pracą. */
const CLOSED: Task["status"][] = ["done", "cancelled"];

const TYPE_TONE: Record<Task["type"], "good" | "warn" | "crit" | "info" | "quiet"> = {
  task: "quiet",
  bug: "crit",
  feature_request: "info",
  chore: "quiet",
};

const PRIORITY_TONE: Record<string, "crit" | "warn" | "quiet"> = {
  p0: "crit",
  p1: "warn",
  p2: "quiet",
  p3: "quiet",
};

/** Odmiana przez liczbę — „1 zadanie", „3 zadania", „7 zadań". */
function zadan(count: number): string {
  if (count === 1) return "zadanie";
  const last = count % 10;
  const teen = count % 100;
  return last >= 2 && last <= 4 && (teen < 12 || teen > 14) ? "zadania" : "zadań";
}

/** Dopełniacz — obie liczby w zdaniu zakresu stoją po liczebniku. */
function produktow(count: number): string {
  return count === 1 ? "produktu" : "produktów";
}

type Filters = {
  text: string;
  priority: string;
  type: string;
  assignee: string;
};

const NO_FILTERS: Filters = { text: "", priority: "", type: "", assignee: "" };

export function ProjectsPage() {
  const queryClient = useQueryClient();
  const demo = useDemo();

  const [scope, setScope] = useState("");
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [showCancelled, setShowCancelled] = useState(false);
  // Otwarte zadanie trzymamy w adresie, nie w samym stanie komponentu:
  // powiadomienie i mail prowadzą wprost do karty, a odświeżenie strony jej
  // nie zamyka. `replace` — kolejne otwarcia nie zaśmiecają historii.
  const [params, setParams] = useSearchParams();
  const openTaskId = params.get("zadanie");
  const openInitiativeId = params.get("inicjatywa");
  const setParam = (name: string, id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set(name, id);
    else next.delete(name);
    setParams(next, { replace: true });
  };
  const setOpenTaskId = (id: string | null) => setParam("zadanie", id);
  const setOpenInitiativeId = (id: string | null) => setParam("inicjatywa", id);
  const [dragged, setDragged] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<{ status: string; beforeId: string | null } | null>(null);
  const [newTitle, setNewTitle] = useState("");

  const products = useQuery({
    queryKey: ["products", demo.key],
    queryFn: () => api.get<{ products: ProductBrief[] }>(demo.scoped("/products")),
  });

  const initiatives = useQuery({
    queryKey: ["initiatives"],
    queryFn: () => api.get<{ initiatives: Initiative[] }>("/initiatives"),
  });

  const tasks = useQuery({
    queryKey: ["tasks"],
    queryFn: () => api.get<{ tasks: Task[] }>("/tasks"),
  });

  // Lista osób jest wspólna dla całego HQ i zmienia się rzadko — trzymamy ją
  // świeżą przez pięć minut, żeby otwarcie każdej karty nie pytało o nią serwera.
  const members = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<{ users: Member[] }>("/users"),
    staleTime: 5 * 60_000,
  });

  const session = useSession();
  const canWrite = session.data?.can_write ?? false;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    void queryClient.invalidateQueries({ queryKey: ["initiatives"] });
  };

  const reorder = useMutation({
    mutationFn: (payload: { status: Task["status"]; ids: string[] }) =>
      api.post("/tasks/reorder", payload),
    onSuccess: invalidate,
  });

  const create = useMutation({
    mutationFn: (title: string) =>
      api.post<{ task: Task }>("/tasks", {
        title,
        product: scope || undefined,
        status: "todo",
      }),
    onSuccess: (data) => {
      setNewTitle("");
      invalidate();
      // Nowa karta ma tylko tytuł. Otwarcie panelu od razu po dodaniu jest
      // różnicą między „dopisałem sobie" a „zadanie ma termin i właściciela".
      setOpenTaskId(data.task.id);
    },
  });

  const productList = products.data?.products ?? [];
  const productIds = useMemo(() => new Set(productList.map((p) => p.id)), [productList]);
  const scoped = productList.find((product) => product.slug === scope) ?? null;

  const byId = (productId: string | null) =>
    productList.find((product) => product.id === productId) ?? null;

  /** Wszystko, co w ogóle wolno pokazać: produkty widoczne + zadania bez produktu. */
  const visible = useMemo(
    () =>
      (tasks.data?.tasks ?? []).filter(
        (task) => task.product_id === null || productIds.has(task.product_id),
      ),
    [tasks.data, productIds],
  );

  const inScope = useMemo(
    () => (scoped ? visible.filter((task) => task.product_id === scoped.id) : visible),
    [visible, scoped],
  );

  const assignees = useMemo(
    () => [...new Set(visible.map((task) => task.assignee).filter(Boolean))] as string[],
    [visible],
  );

  const filtersActive =
    filters.text.trim() !== "" || filters.priority !== "" || filters.type !== "" || filters.assignee !== "";

  const shown = useMemo(() => {
    const needle = filters.text.trim().toLowerCase();
    return inScope.filter((task) => {
      if (filters.priority && task.priority !== filters.priority) return false;
      if (filters.type && task.type !== filters.type) return false;
      if (filters.assignee === "—" && task.assignee) return false;
      if (filters.assignee && filters.assignee !== "—" && task.assignee !== filters.assignee)
        return false;
      if (!needle) return true;
      return `${task.title} ${task.description ?? ""}`.toLowerCase().includes(needle);
    });
  }, [inScope, filters]);

  const openCount = (productId: string) =>
    visible.filter((task) => task.product_id === productId && !CLOSED.includes(task.status)).length;

  const openTotal = visible.filter((task) => !CLOSED.includes(task.status)).length;
  const hidden = visible.length - inScope.length;
  const cancelled = shown.filter((task) => task.status === "cancelled");
  const openTask = openTaskId ? visible.find((task) => task.id === openTaskId) ?? null : null;
  const openInitiative = openInitiativeId
    ? (initiatives.data?.initiatives ?? []).find((row) => row.id === openInitiativeId) ?? null
    : null;

  /**
   * Kolejność, którą wysyłamy na serwer — z całej kolumny, nie z tego, co widać.
   *
   * Przenoszoną kartę bierzemy z `dataTransfer`, nie ze stanu komponentu:
   * stan jest tu tylko po to, żeby karta zbladła w trakcie przeciągania,
   * a tożsamość przeciąganego elementu przenosi samo zdarzenie.
   */
  const drop = (status: Task["status"], beforeId: string | null, id: string) => {
    setDropHint(null);
    setDragged(null);
    const moving = id || dragged;
    if (!moving) return;

    const ids = visible
      .filter((task) => task.status === status)
      .map((task) => task.id)
      .filter((taskId) => taskId !== moving);
    const at = beforeId && beforeId !== moving ? ids.indexOf(beforeId) : -1;
    if (at === -1) ids.push(moving);
    else ids.splice(at, 0, moving);

    reorder.mutate({ status, ids });
  };

  const move = (task: Task, step: -1 | 1) => {
    const index = COLUMNS.indexOf(task.status);
    const target = COLUMNS[index + step];
    if (!target) return;
    const ids = visible
      .filter((item) => item.status === target)
      .map((item) => item.id)
      .filter((id) => id !== task.id);
    reorder.mutate({ status: target, ids: [...ids, task.id] });
  };

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Praca</p>
          <h1>Projekty</h1>
          <p className="page-head__sub">
            Gdzie idzie czas w stosunku do tego, gdzie są pieniądze.
          </p>
        </div>
      </header>

      <section className="scope" aria-labelledby="zakres-tytul">
        <div className="scope__row">
          <p className="scope__eyebrow" id="zakres-tytul">
            Zakres
          </p>
          <div className="scope__options" role="group" aria-labelledby="zakres-tytul">
            <button
              type="button"
              className="scope__option"
              aria-pressed={scope === ""}
              onClick={() => setScope("")}
            >
              Wszystkie produkty
              <span className="scope__count mono" title="otwarte zadania">
                {openTotal}
              </span>
            </button>
            {productList.map((product) => (
              <button
                key={product.slug}
                type="button"
                className="scope__option"
                aria-pressed={scope === product.slug}
                style={{ ["--accent" as string]: product.accent_color }}
                onClick={() => setScope(product.slug)}
              >
                {product.name}
                <span className="scope__count mono" title="otwarte zadania">
                  {openCount(product.id)}
                </span>
              </button>
            ))}
          </div>
        </div>
        <p className="scope__sentence">
          {scoped ? (
            <>
              Widzisz <strong>tylko produkt {scoped.name}</strong>.{" "}
              {hidden > 0
                ? `Poza zakresem zostaje ${hidden} ${zadan(hidden)} z pozostałych ${produktow(productList.length - 1)}.`
                : "Pozostałe produkty nie mają ani jednego zadania."}
            </>
          ) : (
            <>
              Widzisz <strong>całe portfolio</strong> — zadania {productList.length}{" "}
              {produktow(productList.length)} razem z zadaniami nieprzypisanymi do żadnego.
            </>
          )}
        </p>
      </section>

      <div className="stack">
        <Card
          title="Inicjatywy"
          note="Większe kawałki pracy z hipotezą biznesową. Kliknij tytuł, żeby otworzyć szczegóły."
          flush
          action={
            <NewInitiative
              products={productList}
              scope={scope}
              onCreated={() => void queryClient.invalidateQueries({ queryKey: ["initiatives"] })}
            />
          }
        >
          {initiatives.isPending ? (
            <Loading />
          ) : initiatives.isError ? (
            <ErrorState error={initiatives.error} />
          ) : (
            <InitiativeList
              rows={(initiatives.data?.initiatives ?? []).filter(
                (row) =>
                  (row.product_id === null || productIds.has(row.product_id)) &&
                  (!scoped || row.product_id === scoped.id),
              )}
              nameOf={(id) => byId(id)?.name ?? null}
              onOpen={setOpenInitiativeId}
            />
          )}
        </Card>

        <Card
          title="Tablica"
          note="Przeciągnij kartę albo przesuń ją strzałkami. Kliknij tytuł, żeby otworzyć szczegóły."
          action={
            <form
              className="quickadd"
              onSubmit={(event) => {
                event.preventDefault();
                if (newTitle.trim()) create.mutate(newTitle.trim());
              }}
            >
              <input
                aria-label="Nowe zadanie"
                placeholder={
                  scoped ? `Nowe zadanie w ${scoped.name}…` : "Nowe zadanie bez produktu…"
                }
                value={newTitle}
                onChange={(event) => setNewTitle(event.target.value)}
              />
              <button className="btn btn--primary" type="submit" disabled={create.isPending}>
                Dodaj
              </button>
            </form>
          }
        >
          <div className="boardbar">
            <label className="boardbar__search">
              <span className="visually-hidden">Szukaj w tytule i opisie</span>
              <input
                type="search"
                placeholder="Szukaj w tytule i opisie…"
                value={filters.text}
                onChange={(event) => setFilters({ ...filters, text: event.target.value })}
              />
            </label>
            <label>
              <span className="visually-hidden">Priorytet</span>
              <select
                value={filters.priority}
                onChange={(event) => setFilters({ ...filters, priority: event.target.value })}
              >
                <option value="">każdy priorytet</option>
                {PRIORITIES.map((value) => (
                  <option key={value} value={value}>
                    {LABELS.priority[value] ?? value}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="visually-hidden">Typ zadania</span>
              <select
                value={filters.type}
                onChange={(event) => setFilters({ ...filters, type: event.target.value })}
              >
                <option value="">każdy typ</option>
                {TASK_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {LABELS.taskType[value] ?? value}
                  </option>
                ))}
              </select>
            </label>
            {assignees.length > 0 && (
              <label>
                <span className="visually-hidden">Osoba</span>
                <select
                  value={filters.assignee}
                  onChange={(event) => setFilters({ ...filters, assignee: event.target.value })}
                >
                  <option value="">każda osoba</option>
                  <option value="—">bez osoby</option>
                  {assignees.map((person) => (
                    <option key={person} value={person}>
                      {person}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {filtersActive && (
              <button type="button" className="btn btn--quiet" onClick={() => setFilters(NO_FILTERS)}>
                Wyczyść filtry
              </button>
            )}
            <p className="boardbar__count">
              {shown.filter((task) => task.status !== "cancelled").length} {zadan(shown.length)} na
              tablicy
            </p>
          </div>

          {tasks.isPending ? (
            <Loading />
          ) : tasks.isError ? (
            <ErrorState error={tasks.error} />
          ) : (
            <>
              <div className="board">
                {COLUMNS.map((column) => {
                  const items = shown.filter((task) => task.status === column);
                  const isTarget = dropHint?.status === column;
                  return (
                    <section
                      key={column}
                      aria-label={LABELS.taskStatus[column]}
                      className={isTarget ? "column column--target" : "column"}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                        setDropHint({ status: column, beforeId: null });
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        drop(
                          column,
                          dropHint?.status === column ? dropHint.beforeId : null,
                          event.dataTransfer.getData("text/plain"),
                        );
                      }}
                    >
                      <header className="column__head">
                        <span>{LABELS.taskStatus[column]}</span>
                        <span className="mono">{items.length}</span>
                      </header>
                      <div className="column__list">
                        {items.map((task) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            product={byId(task.product_id)}
                            showProduct={!scoped}
                            initiative={
                              initiatives.data?.initiatives.find(
                                (row) => row.id === task.initiative_id,
                              ) ?? null
                            }
                            dragging={dragged === task.id}
                            dropBefore={dropHint?.status === column && dropHint.beforeId === task.id}
                            disabled={reorder.isPending}
                            onOpen={() => setOpenTaskId(task.id)}
                            onDragStart={() => setDragged(task.id)}
                            onDragEnd={() => {
                              setDragged(null);
                              setDropHint(null);
                            }}
                            onDragOverCard={() => setDropHint({ status: column, beforeId: task.id })}
                            onMove={(step) => move(task, step)}
                          />
                        ))}
                        {!items.length && (
                          <p className="column__empty">
                            {filtersActive ? "nic po filtrach" : "pusto"}
                          </p>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>

              {(cancelled.length > 0 || showCancelled) && (
                <div className="board__closed">
                  <button
                    type="button"
                    className="btn btn--quiet"
                    aria-expanded={showCancelled}
                    onClick={() => setShowCancelled((value) => !value)}
                  >
                    {showCancelled ? "Ukryj odrzucone" : `Odrzucone (${cancelled.length})`}
                  </button>
                  {showCancelled && (
                    <ul className="board__cancelled">
                      {cancelled.map((task) => (
                        <li key={task.id}>
                          <button type="button" className="linklike" onClick={() => setOpenTaskId(task.id)}>
                            {task.title}
                          </button>
                          {byId(task.product_id) && (
                            <span className="dim"> · {byId(task.product_id)?.name}</span>
                          )}
                        </li>
                      ))}
                      {!cancelled.length && <li className="dim">nic odrzuconego w tym zakresie</li>}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {openInitiative && (
        <InitiativePanel
          key={openInitiative.id}
          initiative={openInitiative}
          products={productList}
          onClose={() => setOpenInitiativeId(null)}
          onSaved={invalidate}
          onDeleted={() => {
            setOpenInitiativeId(null);
            invalidate();
          }}
        />
      )}

      {openTask && (
        <TaskPanel
          key={openTask.id}
          task={openTask}
          products={productList}
          people={members.data?.users ?? []}
          canWrite={canWrite}
          initiatives={initiatives.data?.initiatives ?? []}
          onClose={() => setOpenTaskId(null)}
          onSaved={invalidate}
          onDeleted={() => {
            setOpenTaskId(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

/* ── Karta na tablicy ─────────────────────────────────────────────────────── */

function TaskCard({
  task,
  product,
  showProduct,
  initiative,
  dragging,
  dropBefore,
  disabled,
  onOpen,
  onDragStart,
  onDragEnd,
  onDragOverCard,
  onMove,
}: {
  task: Task;
  product: ProductBrief | null;
  showProduct: boolean;
  initiative: Initiative | null;
  dragging: boolean;
  dropBefore: boolean;
  disabled: boolean;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOverCard: () => void;
  onMove: (step: -1 | 1) => void;
}) {
  const index = COLUMNS.indexOf(task.status);
  const deadline = due(task.due_date);

  return (
    <article
      className={[
        "task",
        dragging ? "task--dragging" : "",
        dropBefore ? "task--dropbefore" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ ["--accent" as string]: product?.accent_color ?? "var(--hairline-strong)" }}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", task.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDragOverCard();
      }}
    >
      <button type="button" className="task__open" onClick={onOpen}>
        {task.title}
      </button>

      <div className="task__meta">
        <Chip tone={TYPE_TONE[task.type]}>{LABELS.taskType[task.type] ?? task.type}</Chip>
        {(task.priority === "p0" || task.priority === "p1") && (
          <Chip tone={PRIORITY_TONE[task.priority] ?? "quiet"}>{task.priority.toUpperCase()}</Chip>
        )}
        {task.origin === "alert" && <Chip tone="warn">z alertu</Chip>}
        {task.subtasks_total > 0 && (
          <span
            className={
              task.subtasks_done === task.subtasks_total
                ? "task__steps task__steps--full mono"
                : "task__steps mono"
            }
            title={`Kroki: ${task.subtasks_done} z ${task.subtasks_total} odhaczonych`}
          >
            <span aria-hidden="true">✓</span> {task.subtasks_done}/{task.subtasks_total}
          </span>
        )}
        {deadline && (
          <span
            className={deadline.overdue ? "task__due task__due--late mono" : "task__due mono"}
            title={`Termin: ${date(task.due_date)}`}
          >
            {deadline.text}
          </span>
        )}
      </div>

      {(showProduct || task.assignee || initiative) && (
        <p className="task__foot">
          {showProduct && product && <span>{product.name}</span>}
          {showProduct && !product && <span className="task__noproduct">bez produktu</span>}
          {initiative && <span title="Inicjatywa">↳ {initiative.title}</span>}
          {task.assignee && (
            <span className="task__who">
              <Avatar name={task.assignee} />
              {task.assignee}
            </span>
          )}
        </p>
      )}

      <div className="task__move">
        <button
          type="button"
          disabled={index <= 0 || disabled}
          onClick={() => onMove(-1)}
          aria-label={`Przenieś „${task.title}" do kolumny ${LABELS.taskStatus[COLUMNS[index - 1] ?? ""] ?? ""}`}
        >
          ←
        </button>
        <button
          type="button"
          disabled={index >= COLUMNS.length - 1 || disabled}
          onClick={() => onMove(1)}
          aria-label={`Przenieś „${task.title}" do kolumny ${LABELS.taskStatus[COLUMNS[index + 1] ?? ""] ?? ""}`}
        >
          →
        </button>
      </div>
    </article>
  );
}

/* ── Panel szczegółów zadania ─────────────────────────────────────────────── */

function TaskPanel({
  task,
  products,
  people,
  canWrite,
  initiatives,
  onClose,
  onSaved,
  onDeleted,
}: {
  task: Task;
  products: ProductBrief[];
  people: Member[];
  canWrite: boolean;
  initiatives: Initiative[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const productSlug = products.find((product) => product.id === task.product_id)?.slug ?? "";
  const [draft, setDraft] = useState({
    title: task.title,
    description: task.description ?? "",
    status: task.status,
    type: task.type,
    priority: task.priority,
    assignee_id: task.assignee_id ?? "",
    product: productSlug,
    initiative_id: task.initiative_id ?? "",
    due_date: task.due_date ?? "",
    estimate_hours: task.estimate_hours === null ? "" : String(task.estimate_hours),
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = <K extends keyof typeof draft>(field: K, value: (typeof draft)[K]) =>
    setDraft((current) => ({ ...current, [field]: value }));

  // Osobę wysyłamy tylko wtedy, gdy ktoś jej dotknął. Bez tego zapis zmiany
  // terminu kasowałby podpis wpisany ręcznie przed wprowadzeniem kont.
  const assigneeChanged = draft.assignee_id !== (task.assignee_id ?? "");

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/tasks/${task.id}`, {
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        status: draft.status,
        type: draft.type,
        priority: draft.priority,
        ...(assigneeChanged ? { assignee_id: draft.assignee_id || null } : {}),
        product: draft.product || null,
        initiative_id: draft.initiative_id || null,
        due_date: draft.due_date || null,
        estimate_hours: draft.estimate_hours === "" ? null : Number(draft.estimate_hours),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/tasks/${task.id}`),
    onSuccess: onDeleted,
  });

  // Inicjatywa musi należeć do tego samego produktu co zadanie — inaczej
  // postęp inicjatywy liczyłby pracę zrobioną w innej aplikacji.
  const matching = initiatives.filter(
    (row) =>
      !draft.product ||
      row.product_id === products.find((product) => product.slug === draft.product)?.id,
  );

  return (
    <SidePanel
      title={task.title}
      onClose={onClose}
      footer={
        <>
          <div className="sheet__actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={save.isPending || !draft.title.trim()}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Zapisuję…" : "Zapisz zmiany"}
            </button>
            <button type="button" className="btn btn--quiet" onClick={onClose}>
              Odrzuć zmiany
            </button>
          </div>
          <button
            type="button"
            className={confirmDelete ? "btn btn--danger" : "btn btn--quiet"}
            disabled={remove.isPending}
            onClick={() => (confirmDelete ? remove.mutate() : setConfirmDelete(true))}
          >
            {remove.isPending
              ? "Usuwam…"
              : confirmDelete
                ? "Na pewno usuń — bez odwrotu"
                : "Usuń zadanie"}
          </button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <label className="form-grid__wide">
          <span>Tytuł</span>
          <input
            required
            value={draft.title}
            onChange={(event) => set("title", event.target.value)}
          />
        </label>

        <div className="form-grid__wide field">
          <p className="field__label" id="opis-zadania">
            Opis
          </p>
          {canWrite ? (
            <RichTextEditor
              label="Opis zadania"
              placeholder="Co dokładnie trzeba zrobić i po czym poznasz, że skończone."
              value={draft.description}
              onChange={(next) => set("description", next)}
            />
          ) : (
            <RichTextView value={draft.description} />
          )}
        </div>

        <label>
          <span>Kolumna</span>
          <select
            value={draft.status}
            onChange={(event) => set("status", event.target.value as Task["status"])}
          >
            {[...COLUMNS, "cancelled" as const].map((value) => (
              <option key={value} value={value}>
                {LABELS.taskStatus[value] ?? value}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Typ</span>
          <select
            value={draft.type}
            onChange={(event) => set("type", event.target.value as Task["type"])}
          >
            {TASK_TYPES.map((value) => (
              <option key={value} value={value}>
                {LABELS.taskType[value] ?? value}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Priorytet</span>
          <select value={draft.priority} onChange={(event) => set("priority", event.target.value)}>
            {PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {LABELS.priority[value] ?? value}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Osoba</span>
          <select
            value={draft.assignee_id}
            onChange={(event) => set("assignee_id", event.target.value)}
          >
            <option value="">bez osoby</option>
            {people
              .filter((person) => person.is_active || person.id === draft.assignee_id)
              .map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                  {person.is_active ? "" : " — konto wyłączone"}
                </option>
              ))}
          </select>
          {!task.assignee_id && task.assignee && (
            <small>
              Wpisane ręcznie: {task.assignee}. Wybór z listy zastąpi ten podpis i włączy
              powiadomienie dla tej osoby.
            </small>
          )}
        </label>

        {!draft.product && (
          <p className="form-note form-note--warn form-grid__wide" role="status">
            <strong>To zadanie nie ma produktu.</strong> Zostaje jako praca nad samym portfolio
            i nie liczy się do żadnej aplikacji — nie zobaczysz go w zakresie żadnego produktu.
            Jeżeli dotyczy konkretnego produktu, wskaż go poniżej.
          </p>
        )}

        <label>
          <span>Produkt</span>
          <select
            value={draft.product}
            onChange={(event) => {
              set("product", event.target.value);
              set("initiative_id", "");
            }}
          >
            <option value="">bez produktu — praca nad portfolio</option>
            {products.map((product) => (
              <option key={product.slug} value={product.slug}>
                {product.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Inicjatywa</span>
          <select
            value={draft.initiative_id}
            onChange={(event) => set("initiative_id", event.target.value)}
          >
            <option value="">bez inicjatywy</option>
            {matching.map((row) => (
              <option key={row.id} value={row.id}>
                {row.title}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Termin</span>
          <input
            type="date"
            value={draft.due_date}
            onChange={(event) => set("due_date", event.target.value)}
          />
        </label>

        <label>
          <span>Szacunek (godziny)</span>
          <input
            type="number"
            min={0}
            step="0.5"
            value={draft.estimate_hours}
            onChange={(event) => set("estimate_hours", event.target.value)}
          />
        </label>

        {save.isError && (
          <p className="form-error form-grid__wide" role="alert">
            {save.error instanceof Error ? save.error.message : "Nie udało się zapisać"}
          </p>
        )}
        {remove.isError && (
          <p className="form-error form-grid__wide" role="alert">
            {remove.error instanceof Error ? remove.error.message : "Nie udało się usunąć"}
          </p>
        )}
      </form>

      <Subtasks task={task} people={people} canWrite={canWrite} onChanged={onSaved} />

      <dl className="sheet__facts">
        <dt>Skąd się wzięło</dt>
        <dd>{task.origin === "alert" ? "z alertu" : "wpisane ręcznie"}</dd>
        <dt>Zakończone</dt>
        <dd>{task.completed_at ? date(task.completed_at) : "—"}</dd>
      </dl>
    </SidePanel>
  );
}

/* ── Kroki w zadaniu ──────────────────────────────────────────────────────── */

/**
 * Lista do odhaczenia, świadomie płytka.
 *
 * Krok zapisuje się od razu — bez „Zapisz zmiany" pod spodem. To jest różnica
 * między listą, którą się prowadzi w trakcie pracy, a formularzem, który się
 * wypełnia raz i porzuca. Cena jest jedna: każde odhaczenie to żądanie do
 * serwera, więc lista musi być krótka. Przy zadaniu, które wymaga trzydziestu
 * kroków, brakuje nie kroków, tylko drugiego zadania.
 */
function Subtasks({
  task,
  people,
  canWrite,
  onChanged,
}: {
  task: Task;
  people: Member[];
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [title, setTitle] = useState("");
  const field = useRef<HTMLInputElement>(null);

  const add = useMutation({
    mutationFn: (value: string) =>
      api.post<{ subtask: Subtask }>(`/tasks/${task.id}/subtasks`, { title: value }),
    onSuccess: () => {
      setTitle("");
      onChanged();
      // Ognisko zostaje w polu: kroki wypisuje się seriami, a nie po jednym.
      field.current?.focus();
    },
  });

  const change = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/subtasks/${id}`, patch),
    onSuccess: onChanged,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/subtasks/${id}`),
    onSuccess: onChanged,
  });

  const steps = task.subtasks;
  const done = task.subtasks_done;
  const failed = add.isError || change.isError || remove.isError;

  return (
    <section className="subtasks" aria-labelledby="kroki-tytul">
      <header className="subtasks__head">
        <h3 id="kroki-tytul">Kroki</h3>
        {steps.length > 0 && (
          <p className="subtasks__count mono">
            {done} z {steps.length}
          </p>
        )}
      </header>

      {steps.length > 0 && (
        <div className="subtasks__bar">
          <i style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
        </div>
      )}

      {steps.length > 0 ? (
        <ul className="subtasks__list">
          {steps.map((step) => (
            <li key={step.id} className={step.is_done ? "subtask subtask--done" : "subtask"}>
              <label className="subtask__check">
                <span className="visually-hidden">
                  {step.is_done ? "Cofnij odhaczenie" : "Odhacz"}: {step.title}
                </span>
                <input
                  type="checkbox"
                  checked={step.is_done}
                  disabled={!canWrite}
                  onChange={(event) =>
                    change.mutate({ id: step.id, patch: { is_done: event.target.checked } })
                  }
                />
              </label>

              <input
                // Klucz z tytułu: po zapisie pole pokazuje to, co przyjął
                // serwer, a nie to, co zostało w nim po nieudanej zmianie.
                key={step.title}
                className="subtask__title"
                defaultValue={step.title}
                aria-label={`Tytuł kroku: ${step.title}`}
                readOnly={!canWrite}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                }}
                onBlur={(event) => {
                  const value = event.target.value.trim();
                  if (!value) event.target.value = step.title;
                  else if (value !== step.title) {
                    change.mutate({ id: step.id, patch: { title: value } });
                  }
                }}
              />

              <select
                className="subtask__who"
                value={step.assignee_id ?? ""}
                aria-label={`Osoba do kroku: ${step.title}`}
                disabled={!canWrite}
                onChange={(event) =>
                  change.mutate({
                    id: step.id,
                    patch: { assignee_id: event.target.value || null },
                  })
                }
              >
                <option value="">bez osoby</option>
                {people
                  .filter((person) => person.is_active || person.id === step.assignee_id)
                  .map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  ))}
              </select>

              {canWrite && (
                <button
                  type="button"
                  className="subtask__drop"
                  aria-label={`Usuń krok: ${step.title}`}
                  onClick={() => remove.mutate(step.id)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="subtasks__empty">
          Bez kroków. Rozbicie ma sens tam, gdzie praca nie mieści się w jednym podejściu.
        </p>
      )}

      {canWrite && (
        <form
          className="subtasks__add"
          onSubmit={(event) => {
            event.preventDefault();
            if (title.trim()) add.mutate(title.trim());
          }}
        >
          <input
            ref={field}
            value={title}
            aria-label="Nowy krok"
            placeholder="Dodaj krok i naciśnij Enter…"
            onChange={(event) => setTitle(event.target.value)}
          />
          <button type="submit" className="btn btn--quiet" disabled={add.isPending || !title.trim()}>
            Dodaj
          </button>
        </form>
      )}

      {failed && (
        <p className="form-error" role="alert">
          Nie udało się zapisać kroku. Spróbuj jeszcze raz.
        </p>
      )}
    </section>
  );
}

/* ── Inicjatywy ───────────────────────────────────────────────────────────── */

function InitiativeList({
  rows,
  nameOf,
  onOpen,
}: {
  rows: Initiative[];
  nameOf: (productId: string | null) => string | null;
  onOpen: (id: string) => void;
}) {
  if (!rows.length) {
    return (
      <div style={{ padding: "var(--space-5)" }}>
        <Empty
          title="Brak inicjatyw"
          hint="Inicjatywa to kawałek pracy z hipotezą: którą metrykę ma ruszyć i o ile."
        />
      </div>
    );
  }

  return (
    <ul>
      {rows.map((initiative) => (
        <li key={initiative.id} className="initiative">
          <div className="initiative__top">
            <div>
              <button
                type="button"
                className="initiative__title"
                onClick={() => onOpen(initiative.id)}
              >
                {initiative.title}
              </button>
              {initiative.summary && <p className="initiative__summary">{initiative.summary}</p>}
              {initiative.expected_impact && (
                <p className="initiative__impact">
                  {initiative.target_metric ?? "efekt"}: {initiative.expected_impact}
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
              {nameOf(initiative.product_id) && <Chip>{nameOf(initiative.product_id)}</Chip>}
              <Chip
                tone={
                  initiative.status === "in_progress"
                    ? "good"
                    : initiative.status === "shipped"
                      ? "info"
                      : "quiet"
                }
              >
                {LABELS.initiativeStatus[initiative.status] ?? initiative.status}
              </Chip>
              <Chip tone={initiative.priority === "p0" ? "crit" : "quiet"}>
                {initiative.priority.toUpperCase()}
              </Chip>
            </div>
          </div>
          <div className="initiative__bar">
            <i style={{ width: `${Math.round(initiative.progress * 100)}%` }} />
          </div>
          <p className="card__note" style={{ marginTop: "0.4rem" }}>
            {percent(initiative.progress, 0)} z {initiative.task_count} {zadan(initiative.task_count)}
            {initiative.target_date && ` · termin ${date(initiative.target_date)}`}
          </p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Panel inicjatywy — te same pola co przy zakładaniu, plus kasowanie.
 *
 * Kasowanie inicjatywy **nie kasuje jej zadań**. Inicjatywa jest hipotezą,
 * zadanie jest wykonaną pracą; porządkowanie listy pomysłów nie może zabierać
 * ze sobą roboty, która już się wydarzyła. Zadania tracą tylko przypisanie
 * i zostają na tablicy — dlatego przycisk mówi wprost, ilu zadań to dotknie.
 */
function InitiativePanel({
  initiative,
  products,
  onClose,
  onSaved,
  onDeleted,
}: {
  initiative: Initiative;
  products: ProductBrief[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const productSlug = products.find((p) => p.id === initiative.product_id)?.slug ?? "";
  const [draft, setDraft] = useState({
    title: initiative.title,
    summary: initiative.summary ?? "",
    product: productSlug,
    status: initiative.status,
    priority: initiative.priority,
    target_metric: initiative.target_metric ?? "",
    expected_impact: initiative.expected_impact ?? "",
    target_date: initiative.target_date ?? "",
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = <K extends keyof typeof draft>(field: K, value: (typeof draft)[K]) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/initiatives/${initiative.id}`, {
        title: draft.title.trim(),
        summary: draft.summary.trim() || null,
        product: draft.product || null,
        status: draft.status,
        priority: draft.priority,
        target_metric: draft.target_metric.trim() || null,
        expected_impact: draft.expected_impact.trim() || null,
        target_date: draft.target_date || null,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/initiatives/${initiative.id}`),
    onSuccess: onDeleted,
  });

  const zadania = initiative.task_count;

  return (
    <SidePanel
      title={initiative.title}
      onClose={onClose}
      footer={
        <>
          <div className="sheet__actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={save.isPending || !draft.title.trim()}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Zapisuję…" : "Zapisz zmiany"}
            </button>
            <button type="button" className="btn btn--quiet" onClick={onClose}>
              Odrzuć zmiany
            </button>
          </div>
          <button
            type="button"
            className={confirmDelete ? "btn btn--danger" : "btn btn--quiet"}
            disabled={remove.isPending}
            onClick={() => (confirmDelete ? remove.mutate() : setConfirmDelete(true))}
          >
            {remove.isPending
              ? "Usuwam…"
              : confirmDelete
                ? zadania > 0
                  ? `Na pewno usuń — ${zadania} ${zadan(zadania)} zostanie na tablicy`
                  : "Na pewno usuń — bez odwrotu"
                : "Usuń inicjatywę"}
          </button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <label className="form-grid__wide">
          <span>Tytuł</span>
          <input
            required
            value={draft.title}
            onChange={(event) => set("title", event.target.value)}
          />
        </label>

        <label className="form-grid__wide">
          <span>O co chodzi</span>
          <textarea
            rows={3}
            value={draft.summary}
            onChange={(event) => set("summary", event.target.value)}
          />
        </label>

        {!draft.product && (
          <p className="form-note form-note--warn form-grid__wide" role="status">
            <strong>Inicjatywa bez produktu</strong> nie pojawia się w zakresie żadnej aplikacji —
            zostaje jako praca nad samym portfolio. Wskaż produkt, jeżeli hipoteza dotyczy
            konkretnego z nich; zadania inicjatywy pójdą razem z nią.
          </p>
        )}

        <label>
          <span>Produkt</span>
          <select value={draft.product} onChange={(event) => set("product", event.target.value)}>
            <option value="">bez produktu — praca nad portfolio</option>
            {products.map((product) => (
              <option key={product.slug} value={product.slug}>
                {product.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Status</span>
          <select value={draft.status} onChange={(event) => set("status", event.target.value)}>
            {INITIATIVE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {LABELS.initiativeStatus[value] ?? value}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Priorytet</span>
          <select value={draft.priority} onChange={(event) => set("priority", event.target.value)}>
            {PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {LABELS.priority[value] ?? value}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Termin</span>
          <input
            type="date"
            value={draft.target_date}
            onChange={(event) => set("target_date", event.target.value)}
          />
        </label>

        <label>
          <span>Metryka</span>
          <input
            placeholder="trial_conversion"
            value={draft.target_metric}
            onChange={(event) => set("target_metric", event.target.value)}
          />
        </label>

        <label className="form-grid__wide">
          <span>Spodziewany efekt</span>
          <input
            placeholder="z 18% na 25% w dwa miesiące"
            value={draft.expected_impact}
            onChange={(event) => set("expected_impact", event.target.value)}
          />
          <small>
            Bez tego po wdrożeniu nie da się sprawdzić, czy praca cokolwiek zmieniła.
          </small>
        </label>

        {save.isError && (
          <p className="form-error form-grid__wide" role="alert">
            {save.error instanceof Error ? save.error.message : "Nie udało się zapisać"}
          </p>
        )}
        {remove.isError && (
          <p className="form-error form-grid__wide" role="alert">
            {remove.error instanceof Error ? remove.error.message : "Nie udało się usunąć"}
          </p>
        )}
      </form>

      <dl className="sheet__facts">
        <dt>Postęp</dt>
        <dd>
          {percent(initiative.progress, 0)} z {zadania} {zadan(zadania)}
        </dd>
        <dt>Wdrożona</dt>
        <dd>{initiative.shipped_at ? date(initiative.shipped_at) : "—"}</dd>
      </dl>
    </SidePanel>
  );
}

function NewInitiative({
  products,
  scope,
  onCreated,
}: {
  products: ProductBrief[];
  scope: string;
  onCreated: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    summary: "",
    product: scope,
    status: "discovery",
    target_metric: "",
    expected_impact: "",
    target_date: "",
  });

  const create = useMutation({
    mutationFn: () =>
      api.post("/initiatives", {
        title: form.title.trim(),
        summary: form.summary.trim() || undefined,
        product: form.product || undefined,
        status: form.status,
        target_metric: form.target_metric.trim() || undefined,
        expected_impact: form.expected_impact.trim() || undefined,
        target_date: form.target_date || undefined,
      }),
    onSuccess: () => {
      setForm({ ...form, title: "", summary: "", target_metric: "", expected_impact: "" });
      setOpen(false);
      onCreated();
    },
  });

  if (!open) {
    return (
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        Nowa inicjatywa
      </button>
    );
  }

  return (
    <SidePanel
      title="Nowa inicjatywa"
      onClose={() => setOpen(false)}
      footer={
        <div className="sheet__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={create.isPending || !form.title.trim()}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Dodaję…" : "Dodaj inicjatywę"}
          </button>
          <button type="button" className="btn btn--quiet" onClick={() => setOpen(false)}>
            Anuluj
          </button>
        </div>
      }
    >
      <form
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <label className="form-grid__wide">
          <span>Tytuł</span>
          <input
            required
            placeholder="Skrócić drogę od rejestracji do pierwszego kurnika"
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
        </label>
        <label className="form-grid__wide">
          <span>O co chodzi</span>
          <textarea
            rows={3}
            value={form.summary}
            onChange={(event) => setForm({ ...form, summary: event.target.value })}
          />
        </label>
        {!form.product && (
          <p className="form-note form-note--warn form-grid__wide" role="status">
            <strong>Inicjatywa bez produktu</strong> nie pojawi się w zakresie żadnej aplikacji —
            zostanie jako praca nad samym portfolio. Wskaż produkt, jeżeli hipoteza dotyczy
            konkretnego z nich.
          </p>
        )}

        <label>
          <span>Produkt</span>
          <select
            value={form.product}
            onChange={(event) => setForm({ ...form, product: event.target.value })}
          >
            <option value="">bez produktu</option>
            {products.map((product) => (
              <option key={product.slug} value={product.slug}>
                {product.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select
            value={form.status}
            onChange={(event) => setForm({ ...form, status: event.target.value })}
          >
            {INITIATIVE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {LABELS.initiativeStatus[value] ?? value}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Metryka</span>
          <input
            placeholder="trial_conversion"
            value={form.target_metric}
            onChange={(event) => setForm({ ...form, target_metric: event.target.value })}
          />
        </label>
        <label>
          <span>Termin</span>
          <input
            type="date"
            value={form.target_date}
            onChange={(event) => setForm({ ...form, target_date: event.target.value })}
          />
        </label>
        <label className="form-grid__wide">
          <span>Spodziewany efekt</span>
          <input
            placeholder="z 18% na 25% w dwa miesiące"
            value={form.expected_impact}
            onChange={(event) => setForm({ ...form, expected_impact: event.target.value })}
          />
          <small>
            Bez tego po wdrożeniu nie da się sprawdzić, czy praca cokolwiek zmieniła — a lista
            inicjatyw staje się listą rzeczy do zrobienia.
          </small>
        </label>

        {create.isError && (
          <p className="form-error form-grid__wide" role="alert">
            {create.error instanceof Error ? create.error.message : "Nie udało się dodać"}
          </p>
        )}
      </form>
    </SidePanel>
  );
}
